import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ImageIcon } from 'lucide-react'
import { AssetMediaRef } from '@digital-twins/shared-types'

import { ChatFileUpload } from '@/components/chat/ChatFileUpload'
import { CreativeMedia } from '@/components/chat/CreativeMedia'
import { uploadHostedChatStimulus } from '@/components/chat/stimulus-videos'
import { Text } from '@/components/ui/Text'
import { ChatImage } from '@/lib/api'

/** Keep in sync with `MAX_STIMULUS_INPUT_REFS` in
 * `apps/backend/src/workflows/workflow-input-values.ts`. */
export const MAX_STIMULUS_INPUT_REFS = 3

interface ThreadStimulusInputProps {
  inputKey: string
  value: string | null
  onChange: (value: string | null) => void
  disabled: boolean
}

export function hasBoundStimulusValue(
  value: string | null | undefined,
): boolean {
  return parseStimulusValue(value).some(
    (ref) => typeof ref.fileId === 'string' && ref.fileId.trim().length > 0,
  )
}

export function ThreadStimulusInput(props: ThreadStimulusInputProps) {
  const {
    inputKey,
    value,
    onChange,
    disabled,
  } = props

  const { t } = useTranslation('common')
  const { t: tApp } = useTranslation('app')

  const refs = parseStimulusValue(value)
  const [pendingImages, setPendingImages] = useState<Array<ChatImage>>([])
  const canAdd = refs.length < MAX_STIMULUS_INPUT_REFS

  useEffect(() => {
    const hosted = pendingImages
      .map(toStimulusAssetMediaRef)
      .find((ref): ref is AssetMediaRef => ref !== null)
    if (!hosted) {
      return
    }
    const seen = new Set(
      refs
        .map((ref) => ref.fileId?.trim())
        .filter((id): id is string => Boolean(id)),
    )
    if (hosted.fileId && seen.has(hosted.fileId)) {
      setPendingImages([])
      return
    }
    onChange(JSON.stringify([...refs, hosted].slice(0, MAX_STIMULUS_INPUT_REFS)))
    setPendingImages([])
  }, [pendingImages, onChange, refs])

  function handleImagesChange(images: Array<ChatImage>) {
    setPendingImages(images.slice(-1))
  }

  function handleImagesMerge(
    updater: (previous: Array<ChatImage>) => Array<ChatImage>,
  ) {
    setPendingImages((previous) => updater(previous).slice(-1))
  }

  function handleRemove(index: number) {
    const next = refs.filter((_, itemIndex) => itemIndex !== index)
    onChange(next.length > 0 ? JSON.stringify(next) : null)
  }

  const upload = canAdd ? (
    <ChatFileUpload
      images={pendingImages}
      onImagesChange={handleImagesChange}
      onImagesMerge={handleImagesMerge}
      pdfText={null}
      pdfName={null}
      onPdfChange={() => {}}
      maxImages={1}
      disabled={disabled}
      compact
      acceptVideo
      uploadHosted={uploadHostedChatStimulus}
      testIdPrefix={`thread-command-stimulus-upload-${inputKey}`}
    />
  ) : null

  if (refs.length > 0) {
    return (
      <div className="flex flex-col gap-2">
        {refs.length >= 2 ? (
          <Text size="sm" color="muted">
            Each file is its own version.
          </Text>
        ) : null}
        <div className="flex flex-col gap-1.5">
          {refs.map((ref, index) => {
            const mediaUrl = stimulusMediaUrl(ref)
            return (
              <div
                key={`${ref.id}-${index}`}
                className="flex items-center gap-2 rounded-lg border border-border bg-card p-2"
                data-testid={`thread-command-stimulus-tile-${inputKey}-${index}`}
              >
                {mediaUrl ? (
                  <CreativeMedia
                    src={mediaUrl}
                    mimeType={ref.mimeType}
                    alt={ref.label ?? `Stimulus ${index + 1}`}
                    className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted">
                    <ImageIcon
                      className="h-4 w-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </div>
                )}
                <Text size="sm" as="span" className="min-w-0 flex-1 truncate">
                  {ref.label ?? `${ref.kind} stimulus`}
                </Text>
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                  onClick={() => handleRemove(index)}
                  disabled={disabled}
                  data-testid={`thread-command-stimulus-remove-${inputKey}-${index}`}
                >
                  {t('remove')}
                </button>
              </div>
            )
          })}
        </div>
        {upload}
      </div>
    )
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-dashed border-border p-2"
      data-testid={`thread-command-stimulus-slot-${inputKey}`}
    >
      <Text size="sm" color="muted">
        {tApp('threads.detail.command.attachMediaOptional')}
      </Text>
      {upload}
    </div>
  )
}

function parseStimulusValue(
  value: string | null | undefined,
): Array<AssetMediaRef> {
  if (!value?.trim()) {
    return []
  }
  try {
    const parsed = JSON.parse(value) as unknown
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter(
      (entry): entry is AssetMediaRef =>
        typeof entry === 'object' &&
        entry !== null &&
        !Array.isArray(entry) &&
        typeof (entry as AssetMediaRef).id === 'string' &&
        typeof (entry as AssetMediaRef).kind === 'string',
    )
  } catch {
    return []
  }
}

function stimulusMediaUrl(ref: AssetMediaRef): string | null {
  return ref.publicUrl ?? ref.url ?? ref.gcsUri ?? ref.filePath ?? null
}

function isHttpUrl(value: string | undefined): value is string {
  if (!value) {
    return false
  }
  return /^https?:\/\//i.test(value)
}

function kindFromMimeType(
  mimeType: string | undefined,
): 'image' | 'video' | null {
  if (!mimeType) {
    return null
  }
  const normalized = mimeType.toLowerCase()
  if (normalized.startsWith('video/')) {
    return 'video'
  }
  if (normalized.startsWith('image/')) {
    return 'image'
  }
  return null
}

function isChatImageSource(
  source: ChatImage | AssetMediaRef,
): source is ChatImage {
  return 'base64' in source
}

/**
 * Maps a composer tile or a registry `ThreadAssetView.ref` onto the
 * `AssetMediaRef` bind shape. `id` / `fileId` stay `files.token` (contract §5),
 * never the thread-asset token. Missing `fileId` or a non-http public URL is
 * skipped (still-hosting tiles, unhostable kinds).
 */
export function toStimulusAssetMediaRef(
  source: ChatImage | AssetMediaRef | null | undefined,
): AssetMediaRef | null {
  if (!source) {
    return null
  }

  if (isChatImageSource(source)) {
    const fileId = source.fileId?.trim()
    if (!fileId || !isHttpUrl(source.url)) {
      return null
    }
    const kind = kindFromMimeType(source.mimeType)
    if (!kind) {
      return null
    }
    return {
      id: fileId,
      kind,
      fileId,
      mimeType: source.mimeType,
      publicUrl: source.url,
      ...(source.objectKey ? { objectKey: source.objectKey } : {}),
      ...(source.label ? { label: source.label } : {}),
    }
  }

  const fileId = source.fileId?.trim()
  const publicUrl = isHttpUrl(source.publicUrl)
    ? source.publicUrl
    : isHttpUrl(source.url)
      ? source.url
      : undefined
  if (!fileId || !publicUrl) {
    return null
  }
  const kind =
    source.kind === 'video' || source.kind === 'image'
      ? source.kind
      : kindFromMimeType(source.mimeType)
  if (!kind) {
    return null
  }
  return {
    id: fileId,
    kind,
    fileId,
    ...(source.mimeType ? { mimeType: source.mimeType } : {}),
    publicUrl,
    ...(source.objectKey ? { objectKey: source.objectKey } : {}),
    ...(source.label ? { label: source.label } : {}),
  }
}
